import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getAllSections,
  updateSection,
  uploadSectionImage,
  deleteSectionImage,
  purgeOrphanLandingStorageImages,
  ensurePartnerSections,
  ensureFeatureSlideSections,
  createPartnerSection,
  deletePartnerSection,
  isLandingFeatureKey,
  isLandingFeatureSlideKey,
  isLandingPartnerLogoKey,
  featureSlideKeys,
} from '../services/landingPageService';
import type { LandingPageSection, LandingPageSectionUpdate } from '../types/landingPage';
import AdminLandingFaqEditor from './AdminLandingFaqEditor';
import AdminLandingIntroEditor from './AdminLandingIntroEditor';
import type { AdminLandingTabActions } from './adminLandingTabActions';
import LandingImageCropModal, {
  LANDING_IMAGE_PRESETS,
  type LandingImageCropPreset,
} from '../components/landing/LandingImageCropModal';
import BrandLogo from '../components/BrandLogo';
import AutoStatusToast from '../components/common/AutoStatusToast';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import './LandingPage.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const DEFAULT_CTA_LABEL = '버튼';
type CmsTab = 'home' | 'intro' | 'qa';
const HERO_SLIDE_KEYS = ['hero_slide1', 'hero_slide2', 'hero_slide3'] as const;

function isFeatureKey(key: string) {
  return isLandingFeatureKey(key);
}

function isFeatureSlideKey(key: string) {
  return isLandingFeatureSlideKey(key);
}

function isPartnerLogoKey(key: string) {
  return isLandingPartnerLogoKey(key);
}

function nextPartnerSectionKey(sections: LandingPageSection[]): string {
  let max = 0;
  for (const section of sections) {
    const match = /^partner(\d+)$/.exec(section.section_key);
    if (!match) continue;
    max = Math.max(max, Number(match[1]));
  }
  return `partner${max + 1}`;
}

function sortedPartnerLogos(sections: LandingPageSection[]): LandingPageSection[] {
  return sections
    .filter((s) => isPartnerLogoKey(s.section_key))
    .sort((a, b) => a.section_order - b.section_order);
}

function isHeroSlideKey(key: string) {
  return /^hero_slide\d+$/.test(key);
}

function isTopLevelSectionKey(key: string) {
  return (
    key === 'hero' ||
    key === 'cta' ||
    key === 'partners' ||
    isFeatureKey(key)
  );
}

function sectionKind(key: string): 'hero' | 'feature' | 'cta' | 'partners' | 'partnerLogo' {
  if (key === 'hero') return 'hero';
  if (key === 'cta') return 'cta';
  if (key === 'partners') return 'partners';
  if (isPartnerLogoKey(key)) return 'partnerLogo';
  if (isFeatureKey(key)) return 'feature';
  return 'feature';
}

function supportsCta(kind: ReturnType<typeof sectionKind>) {
  return kind === 'hero' || kind === 'feature' || kind === 'cta';
}

function getImageUrl(section: LandingPageSection | undefined | null): string | null {
  if (!section?.image_url) return null;
  return `${section.image_url}?v=${section.image_version}`;
}

function cropPresetForSectionKey(key: string): LandingImageCropPreset {
  if (isHeroSlideKey(key)) return LANDING_IMAGE_PRESETS.hero;
  if (isPartnerLogoKey(key)) return LANDING_IMAGE_PRESETS.partner;
  return LANDING_IMAGE_PRESETS.feature;
}

function buildDrafts(sections: LandingPageSection[]) {
  const formData: Record<string, Partial<LandingPageSection>> = {};
  const ctaEnabled: Record<string, boolean> = {};
  for (const section of sections) {
    formData[section.section_key] = {
      title: section.title,
      subtitle: section.subtitle,
      body: section.body,
      cta_label: section.cta_label,
      cta_link: section.cta_link,
      is_active: section.is_active,
    };
    ctaEnabled[section.section_key] = Boolean(section.cta_label && section.cta_label.trim());
  }
  return { formData, ctaEnabled };
}

/** visible 섹션 순서에 맞춰 section_order 재할당 (히어로 슬라이드·FEATURE 슬라이드·파트너 로고는 부모 뒤에 붙임) */
function withReassignedOrders(
  all: LandingPageSection[],
  visibleOrdered: LandingPageSection[],
): LandingPageSection[] {
  const byKey = new Map(all.map((s) => [s.section_key, s]));
  let order = 1;
  const nextOrder = new Map<string, number>();

  for (const vis of visibleOrdered) {
    nextOrder.set(vis.section_key, order++);
    if (vis.section_key === 'hero') {
      for (const key of HERO_SLIDE_KEYS) {
        if (byKey.has(key)) nextOrder.set(key, order++);
      }
    }
    if (isFeatureKey(vis.section_key)) {
      for (const key of featureSlideKeys(vis.section_key)) {
        if (byKey.has(key)) nextOrder.set(key, order++);
      }
    }
    if (vis.section_key === 'partners') {
      const logos = all
        .filter((s) => isPartnerLogoKey(s.section_key))
        .sort((a, b) => a.section_order - b.section_order);
      for (const logo of logos) {
        nextOrder.set(logo.section_key, order++);
      }
    }
  }

  const leftovers = all
    .filter((s) => !nextOrder.has(s.section_key))
    .sort((a, b) => a.section_order - b.section_order);
  for (const s of leftovers) {
    nextOrder.set(s.section_key, order++);
  }

  return all
    .map((s) => ({
      ...s,
      section_order: nextOrder.get(s.section_key) ?? s.section_order,
    }))
    .sort((a, b) => a.section_order - b.section_order);
}

function SortableHomeSection({
  section,
  orderLabel,
  headingLabel,
  isEditing,
  onStartEdit,
  onStopEdit,
  children,
}: {
  section: LandingPageSection;
  orderLabel: number | string;
  headingLabel: string;
  isEditing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.section_key,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };
  const headId = `cp-admin-landing-head-${section.section_key}`;

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`cp-dashboard-block cp-admin-landing-section-block${
        isDragging ? ' is-dragging' : ''
      }${section.is_active ? '' : ' is-section-inactive'}`}
      aria-labelledby={headId}
    >
      <h3 className="cp-dashboard-block__head" id={headId}>
        <span className="cp-admin-landing-section-head-side cp-admin-landing-section-head-side--left">
          <button
            type="button"
            className="cp-admin-landing-faq-drag"
            title="드래그하여 순서 변경"
            aria-label={`${headingLabel} 순서 변경`}
            {...attributes}
            {...listeners}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </svg>
          </button>
        </span>
        <span className="cp-dashboard-block__head-title">
          {headingLabel}
          <span className="cp-admin-landing-section-order">순서 {orderLabel}</span>
          {!section.is_active ? (
            <span className="cp-admin-landing-status-inactive">비활성</span>
          ) : null}
        </span>
        <span className="cp-admin-landing-section-head-side cp-admin-landing-section-head-side--actions">
          {isEditing ? (
            <button
              type="button"
              className="cp-btn-secondary cp-admin-landing-edit-btn"
              onClick={onStopEdit}
            >
              편집 완료
            </button>
          ) : (
            <button
              type="button"
              className="cp-btn-secondary cp-admin-landing-edit-btn"
              onClick={onStartEdit}
            >
              편집
            </button>
          )}
        </span>
      </h3>

      <div className="cp-dashboard-block__body cp-admin-landing-section-body">{children}</div>
    </article>
  );
}

function SortablePartnerLogoItem({
  id,
  disabled,
  children,
}: {
  id: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
    opacity: isDragging ? 0.92 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`cp-admin-landing-partners-logo-item${isDragging ? ' is-dragging' : ''}`}
      role="listitem"
    >
      {!disabled ? (
        <button
          type="button"
          className="cp-admin-landing-partner-drag"
          aria-label="순서 이동"
          title="드래그하여 순서 변경"
          {...attributes}
          {...listeners}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
          </svg>
        </button>
      ) : null}
      {children}
    </div>
  );
}

const AdminLandingPageCMS: React.FC = () => {
  const [cmsTab, setCmsTab] = useState<CmsTab>('home');
  const [tabActions, setTabActions] = useState<AdminLandingTabActions | null>(null);
  const [sections, setSections] = useState<LandingPageSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editingKeys, setEditingKeys] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState<Record<string, Partial<LandingPageSection>>>({});
  const [ctaEnabled, setCtaEnabled] = useState<Record<string, boolean>>({});
  const [imageFile, setImageFile] = useState<Record<string, File | null>>({});
  const [imagePreview, setImagePreview] = useState<Record<string, string | null>>({});
  const [pendingImageDelete, setPendingImageDelete] = useState<Record<string, boolean>>({});
  /** FEATURE별 표시할 이미지 슬롯 수 (이미지 있는 칸보다 클 수 있음, 최대 3) */
  const [featureSlotCount, setFeatureSlotCount] = useState<Record<string, number>>({});
  const [pendingPartnerDeletes, setPendingPartnerDeletes] = useState<
    Array<{ section_key: string; image_path: string | null }>
  >([]);
  const [cropModal, setCropModal] = useState<{
    sectionKey: string;
    preset: LandingImageCropPreset;
  } | null>(null);
  const { toastState: saveToastState, showSuccess: showSaveSuccess } = useAutoStatusToast(3000);

  const applyServerState = useCallback((data: LandingPageSection[]) => {
    const sorted = [...data].sort((a, b) => a.section_order - b.section_order);
    setSections(sorted);
    const drafts = buildDrafts(sorted);
    setFormData(drafts.formData);
    setCtaEnabled(drafts.ctaEnabled);
    setImageFile({});
    setImagePreview((prev) => {
      Object.values(prev).forEach((url) => {
        if (url && url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(url);
          } catch {
            /* ignore */
          }
        }
      });
      return {};
    });
    setPendingImageDelete({});
    setFeatureSlotCount({});
    setPendingPartnerDeletes([]);
    setCropModal(null);
    setEditingKeys({});
    setDirty(false);
  }, []);

  const loadSections = useCallback(async () => {
    try {
      setLoading(true);
      try {
        await ensurePartnerSections();
      } catch (ensureErr) {
        console.warn('협업기관 섹션 보장 실패:', ensureErr);
      }
      try {
        await ensureFeatureSlideSections();
      } catch (ensureErr) {
        console.warn('FEATURE 이미지 슬롯 보장 실패:', ensureErr);
      }
      const data = await getAllSections();
      applyServerState(data);
      setError(null);
      try {
        await purgeOrphanLandingStorageImages();
      } catch (purgeErr) {
        console.warn('랜딩 고아 이미지 정리 실패:', purgeErr);
      }
    } catch (err) {
      console.error('섹션 로드 실패:', err);
      setError('섹션을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  }, [applyServerState]);

  useEffect(() => {
    void loadSections();
  }, [loadSections]);

  const visibleSections = useMemo(
    () => sections.filter((s) => isTopLevelSectionKey(s.section_key)),
    [sections],
  );

  const visibleIds = useMemo(
    () => visibleSections.map((s) => s.section_key),
    [visibleSections],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const featureOrderByKey = useMemo(() => {
    const map: Record<string, number> = {};
    let n = 0;
    for (const s of visibleSections) {
      if (!isFeatureKey(s.section_key)) continue;
      n += 1;
      map[s.section_key] = n;
    }
    return map;
  }, [visibleSections]);

  const sectionHeadingLabel = (section: LandingPageSection) => {
    if (isFeatureKey(section.section_key)) {
      const n = featureOrderByKey[section.section_key];
      return n ? `FEATURE${n}` : section.section_key.toUpperCase();
    }
    if (section.section_key === 'partners') return 'PARTNERS';
    return section.section_key.toUpperCase();
  };

  const sectionOrderLabel = (section: LandingPageSection) => {
    const idx = visibleSections.findIndex((s) => s.section_key === section.section_key);
    return idx >= 0 ? idx + 1 : section.section_order;
  };

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((prev) => {
      const visible = prev.filter((s) => isTopLevelSectionKey(s.section_key));
      const oldIndex = visible.findIndex((s) => s.section_key === active.id);
      const newIndex = visible.findIndex((s) => s.section_key === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const nextVisible = arrayMove(visible, oldIndex, newIndex);
      return withReassignedOrders(prev, nextVisible);
    });
    setDirty(true);
  };

  const heroSlides = useMemo(
    () =>
      HERO_SLIDE_KEYS.map((key, index) => {
        const found = sections.find((s) => s.section_key === key);
        return (
          found ?? {
            id: `virtual-${key}`,
            section_key: key,
            section_order: 101 + index,
            title: `히어로 슬라이드 ${index + 1}`,
            subtitle: null,
            body: null,
            cta_label: null,
            cta_link: null,
            image_path: null,
            image_url: null,
            image_version: 1,
            is_active: true,
            created_at: '',
            updated_at: '',
          }
        );
      }),
    [sections],
  );

  const featureSlidesByKey = useCallback(
    (featureKey: string) =>
      featureSlideKeys(featureKey).map((key, index) => {
        const found = sections.find((s) => s.section_key === key);
        return (
          found ?? {
            id: `virtual-${key}`,
            section_key: key,
            section_order: 1000 + index,
            title: `이미지 ${index + 1}`,
            subtitle: null,
            body: null,
            cta_label: null,
            cta_link: null,
            image_path: null,
            image_url: null,
            image_version: 1,
            is_active: true,
            created_at: '',
            updated_at: '',
          }
        );
      }),
    [sections],
  );

  const partnerLogos = useMemo(() => sortedPartnerLogos(sections), [sections]);

  const partnerLogoIds = useMemo(
    () => partnerLogos.map((s) => s.section_key),
    [partnerLogos],
  );

  const handlePartnerDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((prev) => {
      const logos = sortedPartnerLogos(prev);
      const oldIndex = logos.findIndex((s) => s.section_key === active.id);
      const newIndex = logos.findIndex((s) => s.section_key === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const moved = arrayMove(logos, oldIndex, newIndex);
      const orderByKey = new Map(moved.map((s, i) => [s.section_key, i]));
      const withPartnerOrder = prev.map((s) =>
        orderByKey.has(s.section_key)
          ? { ...s, section_order: orderByKey.get(s.section_key)! }
          : s,
      );
      const visible = withPartnerOrder.filter((s) => isTopLevelSectionKey(s.section_key));
      return withReassignedOrders(withPartnerOrder, visible);
    });
    setDirty(true);
  };

  const addPartnerLogo = () => {
    const key = nextPartnerSectionKey(sections);
    const indexLabel = partnerLogos.length + 1;
    const newSection: LandingPageSection = {
      id: `virtual-${key}`,
      section_key: key,
      section_order: (partnerLogos[partnerLogos.length - 1]?.section_order ?? 0) + 1,
      title: `협업기관 ${indexLabel}`,
      subtitle: null,
      body: null,
      cta_label: null,
      cta_link: null,
      image_path: null,
      image_url: null,
      image_version: 1,
      is_active: true,
      created_at: '',
      updated_at: '',
    };
    setSections((prev) => {
      const next = [...prev, newSection];
      const visible = next.filter((s) => isTopLevelSectionKey(s.section_key));
      return withReassignedOrders(next, visible);
    });
    setFormData((prev) => ({
      ...prev,
      [key]: {
        title: newSection.title,
        subtitle: null,
        body: null,
        cta_label: null,
        cta_link: '',
        is_active: true,
      },
    }));
    setDirty(true);
  };

  const removePartnerLogo = (logoSection: LandingPageSection) => {
    const key = logoSection.section_key;
    const title =
      (formData[key]?.title || logoSection.title || '협업기관').trim() || '협업기관';
    if (!window.confirm(`「${title}」을(를) 삭제할까요? (저장하기를 눌러야 반영됩니다)`)) {
      return;
    }

    if (!logoSection.id.startsWith('virtual-')) {
      setPendingPartnerDeletes((prev) => {
        if (prev.some((p) => p.section_key === key)) return prev;
        return [...prev, { section_key: key, image_path: logoSection.image_path }];
      });
    }

    clearImageDraft(key);
    setPendingImageDelete((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setFormData((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSections((prev) => {
      const next = prev.filter((s) => s.section_key !== key);
      const visible = next.filter((s) => isTopLevelSectionKey(s.section_key));
      return withReassignedOrders(next, visible);
    });
    setDirty(true);
  };

  const markDirty = () => setDirty(true);

  const patchForm = (sectionKey: string, patch: Partial<LandingPageSection>) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), ...patch },
    }));
    markDirty();
  };

  const revokePreviewUrl = (url: string | null | undefined) => {
    if (url && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    }
  };

  const clearImageDraft = (sectionKey: string) => {
    setImageFile((prev) => {
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });
    setImagePreview((prev) => {
      revokePreviewUrl(prev[sectionKey]);
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });
  };

  const handleDiscard = () => {
    if (dirty && !window.confirm('저장하지 않은 변경을 모두 취소할까요?')) return;
    applyServerState(sections);
  };

  const startEdit = (sectionKey: string) => {
    setEditingKeys((prev) => ({ ...prev, [sectionKey]: true }));
  };

  const stopEdit = (sectionKey: string) => {
    setEditingKeys((prev) => {
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });
  };

  const isSectionEditing = (sectionKey: string) => Boolean(editingKeys[sectionKey]);

  const handleImageChange = (sectionKey: string, file: File | null) => {
    if (!file) {
      clearImageDraft(sectionKey);
      markDirty();
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      window.alert('이미지 파일은 5MB 이하로 업로드해 주세요.');
      return;
    }

    if (!file.type.startsWith('image/')) {
      window.alert('이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    setImageFile((prev) => ({ ...prev, [sectionKey]: file }));
    setPendingImageDelete((prev) => {
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });

    // data URL 대신 object URL — 대용량 미리보기로 레이아웃/메모리가 깨지는 것 방지
    const objectUrl = URL.createObjectURL(file);
    setImagePreview((prev) => {
      revokePreviewUrl(prev[sectionKey]);
      return { ...prev, [sectionKey]: objectUrl };
    });
    markDirty();
  };

  const openImageCropModal = (sectionKey: string) => {
    setCropModal({
      sectionKey,
      preset: cropPresetForSectionKey(sectionKey),
    });
  };

  const slotHasDisplay = useCallback(
    (slide: LandingPageSection) => {
      const key = slide.section_key;
      if (imagePreview[key]) return true;
      if (pendingImageDelete[key]) return false;
      return Boolean(getImageUrl(slide));
    },
    [imagePreview, pendingImageDelete],
  );

  const getVisibleFeatureSlideCount = useCallback(
    (featureKey: string) => {
      const all = featureSlidesByKey(featureKey);
      let last = -1;
      all.forEach((s, i) => {
        if (slotHasDisplay(s)) last = i;
      });
      const fromImages = last >= 0 ? last + 1 : 0;
      const forced = featureSlotCount[featureKey] ?? 0;
      return Math.min(3, Math.max(fromImages, forced));
    },
    [featureSlidesByKey, featureSlotCount, slotHasDisplay],
  );

  const addFeatureImageSlot = (featureKey: string) => {
    const nextCount = Math.min(3, getVisibleFeatureSlideCount(featureKey) + 1);
    setFeatureSlotCount((prev) => ({ ...prev, [featureKey]: nextCount }));
    const slides = featureSlidesByKey(featureKey);
    const newSlide = slides[nextCount - 1];
    if (newSlide) openImageCropModal(newSlide.section_key);
  };

  const handleMarkDeleteImage = (section: LandingPageSection) => {
    const key = section.section_key;
    const hasLocal = Boolean(imageFile[key] || imagePreview[key]);
    const hasServer = Boolean(section.image_path || section.image_url);
    if (!hasLocal && !hasServer) return;
    if (!window.confirm('이미지를 삭제할까요? (저장하기를 눌러야 반영됩니다)')) return;

    clearImageDraft(key);
    if (hasServer) {
      setPendingImageDelete((prev) => ({ ...prev, [key]: true }));
    }
    markDirty();
  };

  const uploadImageForSection = async (sectionKey: string, file: File) => {
    const section = sections.find((s) => s.section_key === sectionKey);
    if (section?.image_path) {
      try {
        await deleteSectionImage(section.image_path);
      } catch (err) {
        console.warn('기존 이미지 삭제 실패:', err);
      }
    }

    const { path, url } = await uploadSectionImage(sectionKey, file);
    await updateSection(sectionKey, {
      image_path: path,
      image_url: url,
      image_version: (section?.image_version || 0) + 1,
    });
  };

  const handleSaveAll = async () => {
    if (!dirty) return;

    try {
      setSaving(true);

      for (const pending of pendingPartnerDeletes) {
        await deletePartnerSection(pending.section_key, pending.image_path);
      }

      const virtualPartners = sections.filter(
        (s) => s.id.startsWith('virtual-') && isPartnerLogoKey(s.section_key),
      );
      for (const section of virtualPartners) {
        const key = section.section_key;
        const draft = formData[key] || {};
        await createPartnerSection({
          section_key: key,
          section_order: section.section_order,
          title: (draft.title || section.title || '협업기관').trim() || '협업기관',
          cta_link: (draft.cta_link || '').trim() || null,
          is_active: draft.is_active !== false,
        });
      }

      const imageKeys = Array.from(
        new Set<string>([
          ...Object.keys(imageFile).filter((k) => imageFile[k]),
          ...Object.keys(pendingImageDelete).filter((k) => pendingImageDelete[k]),
        ]),
      );

      for (const key of imageKeys) {
        if (pendingImageDelete[key] && !imageFile[key]) {
          const section = sections.find((s) => s.section_key === key);
          if (section?.image_path) {
            await deleteSectionImage(section.image_path);
          }
          await updateSection(key, {
            image_path: null,
            image_url: null,
          });
        } else if (imageFile[key]) {
          await uploadImageForSection(key, imageFile[key]!);
        }
      }

      const sectionsAfterCreate = await getAllSections();
      const sectionByKey = new Map(sectionsAfterCreate.map((s) => [s.section_key, s]));

      for (const section of sections) {
        if (section.id.startsWith('virtual-')) continue;
        if (pendingPartnerDeletes.some((p) => p.section_key === section.section_key)) continue;
        const key = section.section_key;
        if (!sectionByKey.has(key)) continue;

        if (isHeroSlideKey(key) || isFeatureSlideKey(key)) {
          await updateSection(key, { section_order: section.section_order });
          continue;
        }

        const updates: LandingPageSectionUpdate = {
          ...(formData[key] || {}),
          section_order: section.section_order,
        };

        if (isPartnerLogoKey(key)) {
          updates.cta_link = (updates.cta_link || '').trim() || null;
          updates.cta_label = null;
        } else if (supportsCta(sectionKind(key))) {
          if (ctaEnabled[key]) {
            const label = (updates.cta_label || '').trim() || DEFAULT_CTA_LABEL;
            updates.cta_label = label;
            updates.cta_link = (updates.cta_link || '').trim() || null;
          } else {
            updates.cta_label = null;
            updates.cta_link = null;
          }
        }

        await updateSection(key, updates);
      }

      // 신규 파트너도 최종 section_order·폼 반영
      for (const section of virtualPartners) {
        const key = section.section_key;
        const draft = formData[key] || {};
        await updateSection(key, {
          title: (draft.title || section.title || '협업기관').trim() || '협업기관',
          cta_link: (draft.cta_link || '').trim() || null,
          cta_label: null,
          is_active: draft.is_active !== false,
          section_order: section.section_order,
        });
      }

      const fresh = await getAllSections();
      applyServerState(fresh);
      try {
        await purgeOrphanLandingStorageImages();
      } catch (purgeErr) {
        console.warn('랜딩 고아 이미지 정리 실패:', purgeErr);
      }
      showSaveSuccess('랜딩 페이지가 저장되었습니다.');
    } catch (err) {
      console.error('저장 실패:', err);
      const detail =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message || '')
          : err instanceof Error
            ? err.message
            : '';
      window.alert(
        detail
          ? `저장에 실패했습니다.\n${detail}`
          : '저장에 실패했습니다. 잠시 후 다시 시도해 주세요.',
      );
    } finally {
      setSaving(false);
    }
  };

  const renderCtaRow = (
    section: LandingPageSection,
    data: Partial<LandingPageSection>,
    variant: 'hero' | 'feature' | 'cta',
    isEditing: boolean,
  ) => {
    const key = section.section_key;
    const enabled = Boolean(ctaEnabled[key]);
    const label = (data.cta_label || '').trim() || DEFAULT_CTA_LABEL;
    const link = data.cta_link || '';

    const btnClass =
      variant === 'cta'
        ? 'landing-cta-btn'
        : variant === 'hero'
          ? 'cp-admin-landing-hero-cta'
          : 'cp-admin-landing-feature-cta';

    if (!isEditing) {
      if (!enabled) return null;
      return (
        <div className={`cp-admin-landing-cta-row cp-admin-landing-cta-row--${variant}`}>
          <div className="cp-admin-landing-cta-main">
            <span className={btnClass} aria-hidden>
              {label}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className={`cp-admin-landing-cta-row cp-admin-landing-cta-row--${variant} is-editing`}>
        <div
          className={`cp-admin-landing-cta-btn-block ${btnClass}${enabled ? '' : ' is-ghost'}`}
        >
          <button
            type="button"
            className={`cp-admin-landing-cta-check${enabled ? ' is-done' : ''}`}
            title="버튼 표시"
            aria-pressed={enabled}
            aria-label="버튼 표시"
            onClick={() => {
              const on = !enabled;
              setCtaEnabled((prev) => ({ ...prev, [key]: on }));
              if (on) {
                patchForm(key, {
                  ...data,
                  cta_label: data.cta_label?.trim() ? data.cta_label : DEFAULT_CTA_LABEL,
                });
              } else {
                markDirty();
              }
            }}
          >
            {enabled ? (
              <svg
                className="cp-admin-landing-cta-check-mark"
                viewBox="0 0 20 20"
                width="13"
                height="13"
                aria-hidden
                focusable="false"
              >
                <path
                  d="M5.05 10.35 L9.25 14.65 L15.35 5.55"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.15"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : null}
          </button>

          <input
            type="text"
            className="cp-admin-landing-edit-input cp-admin-landing-edit-input--btn-label"
            value={data.cta_label || DEFAULT_CTA_LABEL}
            onChange={(e) => patchForm(key, { ...data, cta_label: e.target.value })}
            aria-label="버튼 텍스트"
            placeholder="버튼 텍스트"
            disabled={!enabled}
          />
        </div>

        <input
          type="text"
          className="cp-admin-landing-edit-input cp-admin-landing-cta-link"
          value={link}
          onChange={(e) => patchForm(key, { ...data, cta_link: e.target.value })}
          aria-label="버튼 링크"
          placeholder="/login 또는 URL"
          disabled={!enabled}
        />
      </div>
    );
  };

  const renderImageSlot = (
    section: LandingPageSection,
    isEditing: boolean,
    options?: { compact?: boolean; logo?: boolean },
  ) => {
    const key = section.section_key;
    const displayUrl =
      imagePreview[key] || (pendingImageDelete[key] ? null : getImageUrl(section));
    const compact = options?.compact;
    const logo = options?.logo;
    const panelMod = logo
      ? ' cp-admin-landing-image-panel--logo'
      : compact
        ? ' cp-admin-landing-image-panel--compact'
        : '';

    return (
      <div className={`cp-admin-landing-image-panel${panelMod}`}>
        <div className={`cp-admin-landing-image-block${isEditing ? ' is-editing' : ''}`}>
          {displayUrl ? (
            <div
              className={`landing-feature-image-wrap cp-admin-landing-image-wrap${
                logo ? ' cp-admin-landing-image-wrap--logo' : ''
              }`}
            >
              <img
                src={displayUrl}
                alt={section.title || ''}
                className={`landing-feature-image${logo ? ' cp-admin-landing-image--logo' : ''}`}
              />
              {isEditing ? (
                <button
                  type="button"
                  className="cp-btn-danger cp-admin-landing-image-corner-remove"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMarkDeleteImage(section);
                  }}
                  disabled={saving}
                >
                  삭제
                </button>
              ) : null}
            </div>
          ) : (
            <div
              className={`landing-feature-image-placeholder cp-admin-landing-image-placeholder${
                logo ? ' cp-admin-landing-image-placeholder--logo' : ''
              }`}
            >
              <span className="landing-feature-image-placeholder-text">
                {logo ? '로고' : '이미지 준비중'}
              </span>
            </div>
          )}

          {isEditing ? (
            <div className="cp-admin-landing-image-block-actions">
              <button
                type="button"
                className="cp-admin-landing-file-btn"
                disabled={saving}
                onClick={() => openImageCropModal(key)}
              >
                이미지 선택
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderPreviewBody = (section: LandingPageSection) => {
    const isEditing = isSectionEditing(section.section_key);
    const data = formData[section.section_key] || section;
    const kind = sectionKind(section.section_key);

    const updateField = (field: keyof LandingPageSection, value: string) => {
      patchForm(section.section_key, {
        ...data,
        [field]: value,
      } as Partial<LandingPageSection>);
    };

    if (kind === 'partners') {
      return (
        <div className="cp-admin-landing-preview cp-admin-landing-preview--partners">
          <div className="cp-admin-landing-preview-text cp-admin-landing-preview-text--center">
            {isEditing ? (
              <>
                <input
                  type="text"
                  className="cp-admin-landing-edit-input landing-partners-title"
                  value={data.title || ''}
                  onChange={(e) => updateField('title', e.target.value)}
                  aria-label="제목"
                  placeholder="협업기관"
                />
                <input
                  type="text"
                  className="cp-admin-landing-edit-input landing-partners-subtitle"
                  value={data.subtitle || ''}
                  onChange={(e) => updateField('subtitle', e.target.value)}
                  aria-label="부제목"
                  placeholder="함께하는 기관·학교"
                />
              </>
            ) : (
              <>
                <h2 className="landing-partners-title">{data.title || '협업기관'}</h2>
                {data.subtitle ? (
                  <p className="landing-partners-subtitle">{data.subtitle}</p>
                ) : null}
              </>
            )}
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handlePartnerDragEnd}
          >
            <SortableContext items={partnerLogoIds} strategy={rectSortingStrategy}>
              <div className="cp-admin-landing-partners-logos" role="list">
                {partnerLogos.map((logoSection, index) => {
                  const logoData = formData[logoSection.section_key] || logoSection;
                  const logoTitle = logoData.title || `협업기관 ${index + 1}`;
                  const logoLink = logoData.cta_link || '';
                  const logoActive = logoData.is_active !== false;
                  return (
                    <SortablePartnerLogoItem
                      key={logoSection.section_key}
                      id={logoSection.section_key}
                      disabled={!isEditing || saving}
                    >
                      <div
                        className={`cp-admin-landing-partners-logo-stack${
                          logoActive ? '' : ' is-inactive'
                        }`}
                      >
                        <button
                          type="button"
                          className={`cp-admin-landing-partner-check${logoActive ? ' is-done' : ''}`}
                          title={logoActive ? '사용 중 (클릭하여 숨김)' : '미사용 (클릭하여 표시)'}
                          aria-pressed={logoActive}
                          aria-label={`${logoTitle} 사용 여부`}
                          disabled={!isEditing || saving}
                          onClick={() =>
                            patchForm(logoSection.section_key, {
                              ...logoData,
                              is_active: !logoActive,
                            })
                          }
                        >
                          {logoActive ? (
                            <svg
                              className="cp-admin-landing-cta-check-mark"
                              viewBox="0 0 20 20"
                              width="13"
                              height="13"
                              aria-hidden
                              focusable="false"
                            >
                              <path
                                d="M5.05 10.35 L9.25 14.65 L15.35 5.55"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3.15"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : null}
                        </button>
                        {renderImageSlot(logoSection, isEditing, { logo: true })}
                        {isEditing ? (
                          <>
                            <input
                              type="text"
                              className="cp-admin-landing-edit-input cp-admin-landing-edit-input--partner-title"
                              value={logoTitle}
                              onChange={(e) =>
                                patchForm(logoSection.section_key, {
                                  ...logoData,
                                  title: e.target.value,
                                })
                              }
                              aria-label={`협업기관 ${index + 1} 이름`}
                              placeholder={`협업기관 ${index + 1}`}
                            />
                            <input
                              type="text"
                              className="cp-admin-landing-edit-input cp-admin-landing-edit-input--partner-link"
                              value={logoLink}
                              onChange={(e) =>
                                patchForm(logoSection.section_key, {
                                  ...logoData,
                                  cta_link: e.target.value,
                                })
                              }
                              aria-label={`${logoTitle} 링크`}
                              placeholder="https://…"
                              inputMode="url"
                              autoComplete="url"
                            />
                            <button
                              type="button"
                              className="cp-btn-secondary cp-admin-landing-partner-remove"
                              disabled={saving}
                              onClick={() => removePartnerLogo(logoSection)}
                            >
                              삭제
                            </button>
                          </>
                        ) : (
                          <>
                            <p className="cp-admin-landing-partner-title">{logoTitle}</p>
                            {logoLink.trim() ? (
                              <p className="cp-admin-landing-partner-link">{logoLink.trim()}</p>
                            ) : null}
                          </>
                        )}
                      </div>
                    </SortablePartnerLogoItem>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>

          {isEditing ? (
            <div className="cp-admin-landing-partners-actions">
              <button
                type="button"
                className="cp-btn-secondary"
                disabled={saving}
                onClick={addPartnerLogo}
              >
                협업기관 추가
              </button>
            </div>
          ) : null}
        </div>
      );
    }

    if (kind === 'cta') {
      return (
        <div className="cp-admin-landing-preview cp-admin-landing-preview--cta">
          <div className="landing-cta-content cp-admin-landing-cta-inner">
            {isEditing ? (
              <>
                <textarea
                  className="cp-admin-landing-edit-input landing-cta-title"
                  value={data.title || ''}
                  onChange={(e) => updateField('title', e.target.value)}
                  aria-label="제목"
                  rows={2}
                />
                <textarea
                  className="cp-admin-landing-edit-input landing-cta-subtitle"
                  value={data.body || data.subtitle || ''}
                  onChange={(e) => updateField('body', e.target.value)}
                  aria-label="본문"
                  rows={2}
                  placeholder="안내 문구"
                />
              </>
            ) : (
              <>
                <h2 className="landing-cta-title">{data.title}</h2>
                {(data.body || data.subtitle) && (
                  <p className="landing-cta-subtitle">{data.body || data.subtitle}</p>
                )}
              </>
            )}
            {renderCtaRow(section, data, 'cta', isEditing)}
          </div>
        </div>
      );
    }

    if (kind === 'hero') {
      const headline = data.subtitle || data.title || '';
      return (
        <div className="cp-admin-landing-preview cp-admin-landing-preview--hero">
          <div className="cp-admin-landing-hero-slides" aria-label="히어로 캐러셀 이미지 3장">
            {heroSlides.map((slide) => (
              <div key={slide.section_key} className="cp-admin-landing-hero-slide-col">
                {renderImageSlot(slide, isEditing, { compact: true })}
              </div>
            ))}
          </div>

          <div className="cp-admin-landing-preview-text cp-admin-landing-preview-text--center cp-admin-landing-hero-copy">
            {isEditing ? (
              <>
                <input
                  type="text"
                  className="cp-admin-landing-edit-input cp-admin-landing-hero-brand"
                  value={data.title || ''}
                  onChange={(e) => updateField('title', e.target.value)}
                  aria-label="제목"
                  placeholder="제목"
                />
                <textarea
                  className="cp-admin-landing-edit-input cp-admin-landing-hero-title"
                  value={headline}
                  onChange={(e) =>
                    patchForm(section.section_key, { ...data, subtitle: e.target.value })
                  }
                  aria-label="부제목"
                  rows={2}
                  placeholder="부제목"
                />
                <textarea
                  className="cp-admin-landing-edit-input cp-admin-landing-hero-body"
                  value={data.body || ''}
                  onChange={(e) => updateField('body', e.target.value)}
                  aria-label="내용"
                  rows={3}
                  placeholder="내용"
                />
              </>
            ) : (
              <>
                <p className="cp-admin-landing-hero-brand">
                  <BrandLogo className="cp-brand-logo--hero" />
                </p>
                <h1 className="cp-admin-landing-hero-title">{headline}</h1>
                {data.body ? <p className="cp-admin-landing-hero-body">{data.body}</p> : null}
              </>
            )}
            {renderCtaRow(section, data, 'hero', isEditing)}
          </div>
        </div>
      );
    }

    return (
      <div className="cp-admin-landing-preview cp-admin-landing-preview--feature">
        <div className="cp-admin-landing-feature-frame">
          <div className="landing-feature-content">
            <div className="cp-admin-landing-preview-text landing-feature-text">
              {isEditing ? (
                <>
                  <textarea
                    className="cp-admin-landing-edit-input landing-feature-title"
                    value={data.title || ''}
                    onChange={(e) => updateField('title', e.target.value)}
                    aria-label="제목"
                    rows={2}
                    placeholder="제목"
                  />
                  <textarea
                    className="cp-admin-landing-edit-input landing-feature-subtitle"
                    value={data.subtitle || ''}
                    onChange={(e) => updateField('subtitle', e.target.value)}
                    aria-label="부제목"
                    rows={1}
                    placeholder="부제목"
                  />
                  <textarea
                    className="cp-admin-landing-edit-input landing-feature-body"
                    value={data.body || ''}
                    onChange={(e) => updateField('body', e.target.value)}
                    aria-label="본문"
                    rows={3}
                    placeholder="본문"
                  />
                </>
              ) : (
                <>
                  <h2 className="landing-feature-title">{data.title}</h2>
                  {data.subtitle ? (
                    <p className="landing-feature-subtitle">{data.subtitle}</p>
                  ) : null}
                  {data.body ? <p className="landing-feature-body">{data.body}</p> : null}
                </>
              )}
              {renderCtaRow(section, data, 'feature', isEditing)}
            </div>
            <div
              className="cp-admin-landing-feature-slides"
              aria-label="특징 이미지 최대 3장"
            >
              {featureSlidesByKey(section.section_key)
                .slice(0, getVisibleFeatureSlideCount(section.section_key))
                .map((slide, index) => (
                  <div key={slide.section_key} className="cp-admin-landing-feature-slide-col">
                    <span className="cp-admin-landing-image-slot-label">이미지 {index + 1}</span>
                    {renderImageSlot(slide, isEditing, { compact: true })}
                  </div>
                ))}
              {isEditing && getVisibleFeatureSlideCount(section.section_key) < 3 ? (
                <button
                  type="button"
                  className="cp-admin-landing-feature-add-slot"
                  disabled={saving}
                  onClick={() => addFeatureImageSlot(section.section_key)}
                >
                  <span aria-hidden>+</span>
                  이미지 추가
                </button>
              ) : null}
              {!isEditing && getVisibleFeatureSlideCount(section.section_key) === 0 ? (
                <div className="landing-feature-image-placeholder cp-admin-landing-image-placeholder">
                  <span className="landing-feature-image-placeholder-text">이미지 준비중</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <section
        className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-landing-page"
        aria-labelledby="cp-admin-landing-title"
      >
        <div className="cp-page-title-row cp-admin-notices-page-title-row">
          <div className="cp-admin-legal-title-stack">
            <h2 id="cp-admin-landing-title">랜딩 페이지 관리</h2>
          </div>
        </div>
        <p className="cp-dashboard-placeholder" role="status">
          로딩 중…
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section
        className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-landing-page"
        aria-labelledby="cp-admin-landing-title"
      >
        <div className="cp-page-title-row cp-admin-notices-page-title-row">
          <div className="cp-admin-legal-title-stack">
            <h2 id="cp-admin-landing-title">랜딩 페이지 관리</h2>
          </div>
        </div>
        <p className="cp-dashboard-placeholder" role="alert">
          {error}
        </p>
        <button type="button" className="cp-btn-primary" onClick={() => void loadSections()}>
          다시 시도
        </button>
      </section>
    );
  }

  return (
    <section
      className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-landing-page"
      aria-labelledby="cp-admin-landing-title"
    >
      <div className="cp-page-title-row cp-admin-notices-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-admin-landing-title">랜딩 페이지 관리</h2>
          <p className="cp-admin-legal-subtitle">
            저장은 수파베이스 초안입니다. Home·서비스 소개·Q&A 공개 화면은 배포할 때 버셀에 발행됩니다.
          </p>
          <div className="cp-admin-landing-cms-tabs" role="tablist" aria-label="랜딩 관리 카테고리">
            <button
              type="button"
              role="tab"
              aria-selected={cmsTab === 'home'}
              className={`cp-admin-landing-cms-tab${cmsTab === 'home' ? ' is-active' : ''}`}
              onClick={() => {
                setTabActions(null);
                setCmsTab('home');
              }}
            >
              Home
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={cmsTab === 'intro'}
              className={`cp-admin-landing-cms-tab${cmsTab === 'intro' ? ' is-active' : ''}`}
              onClick={() => {
                setTabActions(null);
                setCmsTab('intro');
              }}
            >
              서비스 소개
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={cmsTab === 'qa'}
              className={`cp-admin-landing-cms-tab${cmsTab === 'qa' ? ' is-active' : ''}`}
              onClick={() => {
                setTabActions(null);
                setCmsTab('qa');
              }}
            >
              Q&amp;A
            </button>
          </div>
        </div>
        {cmsTab === 'home' ? (
          <div className="cp-admin-landing-title-actions">
            <button
              type="button"
              className="cp-btn-secondary"
              onClick={handleDiscard}
              disabled={saving || !dirty}
            >
              변경 취소
            </button>
            <button
              type="button"
              className="cp-btn-save-all cp-admin-landing-top-save-btn"
              onClick={() => void handleSaveAll()}
              disabled={saving || !dirty}
              title={dirty ? '저장하기' : '저장할 변경사항 없음'}
            >
              {saving ? '저장 중…' : dirty ? '저장하기' : '변경사항 없음'}
            </button>
          </div>
        ) : tabActions ? (
          <div className="cp-admin-landing-title-actions">
            {tabActions.onAdd ? (
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={tabActions.onAdd}
                disabled={tabActions.saving}
              >
                {tabActions.addLabel ?? '추가'}
              </button>
            ) : null}
            <button
              type="button"
              className="cp-btn-secondary"
              onClick={tabActions.onDiscard}
              disabled={tabActions.saving || !tabActions.dirty}
            >
              변경 취소
            </button>
            <button
              type="button"
              className="cp-btn-save-all cp-admin-landing-top-save-btn"
              onClick={tabActions.onSave}
              disabled={tabActions.saving || !tabActions.dirty}
              title={tabActions.dirty ? '저장하기' : '저장할 변경사항 없음'}
            >
              {tabActions.saving
                ? '저장 중…'
                : tabActions.dirty
                  ? '저장하기'
                  : '변경사항 없음'}
            </button>
          </div>
        ) : null}
      </div>

      {cmsTab === 'qa' ? (
        <AdminLandingFaqEditor onActionsChange={setTabActions} />
      ) : cmsTab === 'intro' ? (
        <AdminLandingIntroEditor onActionsChange={setTabActions} />
      ) : (
        <>
          {dirty ? (
            <p className="cp-admin-landing-dirty-hint" role="status">
              저장되지 않은 변경이 있습니다. 「저장하기」를 눌러 반영하세요.
            </p>
          ) : (
            <p className="cp-admin-landing-dirty-hint" role="note">
              섹션 왼쪽 ≡ 아이콘을 드래그하면 표시 순서를 바꿀 수 있습니다.
            </p>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleSectionDragEnd}
          >
            <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
              <div className="cp-admin-landing-sections">
                {visibleSections.map((section) => {
                  const isEditing = isSectionEditing(section.section_key);
                  return (
                    <SortableHomeSection
                      key={section.section_key}
                      section={section}
                      orderLabel={sectionOrderLabel(section)}
                      headingLabel={sectionHeadingLabel(section)}
                      isEditing={isEditing}
                      onStartEdit={() => startEdit(section.section_key)}
                      onStopEdit={() => stopEdit(section.section_key)}
                    >
                      {renderPreviewBody(section)}
                    </SortableHomeSection>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </>
      )}

      {cmsTab === 'home' ? (
        <button
          type="button"
          className="cp-fab-save"
          onClick={() => void handleSaveAll()}
          disabled={saving || !dirty}
          aria-label="저장하기"
          title={dirty ? '저장하기' : '저장할 변경사항 없음'}
        >
          <img
            src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
            alt=""
            className="cp-fab-save-icon"
          />
        </button>
      ) : tabActions ? (
        <>
          <button
            type="button"
            className={`cp-fab-save${tabActions.onAdd ? ' cp-admin-landing-fab-save--above-add' : ''}`}
            onClick={tabActions.onSave}
            disabled={tabActions.saving || !tabActions.dirty}
            aria-label="저장하기"
            title={tabActions.dirty ? '저장하기' : '저장할 변경사항 없음'}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
              alt=""
              className="cp-fab-save-icon"
            />
          </button>
          {tabActions.onAdd ? (
            <button
              type="button"
              className="cp-admin-landing-fab-add"
              onClick={tabActions.onAdd}
              disabled={tabActions.saving}
              aria-label={tabActions.addLabel ?? '추가'}
              title={tabActions.addLabel ?? '추가'}
            >
              +
            </button>
          ) : null}
        </>
      ) : null}

      <AutoStatusToast state={saveToastState} />
      <LandingImageCropModal
        open={Boolean(cropModal)}
        preset={cropModal?.preset ?? LANDING_IMAGE_PRESETS.feature}
        onClose={() => setCropModal(null)}
        onConfirm={(file) => {
          if (!cropModal) return;
          handleImageChange(cropModal.sectionKey, file);
        }}
      />
    </section>
  );
};

export default AdminLandingPageCMS;
